import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HashLink } from "@/components/landing/HashLink";
import { EmailLink } from "@/components/EmailLink";

const LEARNER_PLANS = [
  { name: "Single course", prefix: "From", price: "₦20,000", desc: "Pay once for a specific skill." },
  { name: "Learning programme", prefix: "From", price: "₦70,000", desc: "Structured progression at a package price." },
  { name: "Learning membership", price: "₦20,000", unit: "/mo", desc: "For ongoing development, included content and recurring practice." },
];

const ORG_PLANS = [
  { name: "Team", price: "₦7,000", unit: "/seat/mo" },
  { name: "Business", price: "₦15,000", unit: "/seat/mo" },
  { name: "Enterprise", price: "Custom", contact: true },
];

function PlanCard({ plan, testid }) {
  return (
    <li className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 sm:p-8" data-testid={testid}>
      <h4 className="font-display text-lg font-bold text-slate-900">{plan.name}</h4>
      <p className="mt-4 text-slate-900">
        {plan.prefix && <span className="mr-1.5 text-sm text-slate-700">{plan.prefix}</span>}
        <span className="font-display text-3xl font-extrabold" data-testid={`${testid}-price`}>{plan.price}</span>
        {plan.unit && <span className="text-sm text-slate-700">{plan.unit}</span>}
      </p>
      {plan.desc && <p className="mt-3 text-sm leading-relaxed text-slate-700">{plan.desc}</p>}
      {plan.contact && (
        <EmailLink subject="Crowlearn Organisation Enquiry" className="mt-2 w-fit text-sm font-semibold" testid={`${testid}-contact`}>
          Contact us <ArrowRight className="h-4 w-4" />
        </EmailLink>
      )}
    </li>
  );
}

export function Pricing() {
  return (
    <section id="pricing" className="scroll-mt-20 border-b border-slate-200" data-testid="pricing-section">
      <div className="mx-auto max-w-6xl px-4 pt-16 sm:px-6 lg:pt-24">
        <p className="font-mono-label mb-3 text-xs uppercase tracking-[0.2em] text-[#222d99]">Pricing</p>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Learning that fits your goals</h2>
        <p className="mt-3 max-w-2xl text-base text-slate-700">Build one skill, follow a learning path or develop an entire team.</p>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6" data-testid="pricing-learners">
        <h3 className="text-xl font-semibold text-slate-900">For individual learners</h3>
        <ul className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {LEARNER_PLANS.map((p, i) => <PlanCard key={p.name} plan={p} testid={`pricing-learner-card-${i}`} />)}
        </ul>
        <Button asChild size="lg" className="mt-8 rounded-full bg-[#222d99] px-7 hover:bg-[#1a237e]" data-testid="pricing-explore-courses-btn">
          <HashLink hash="#courses">Explore Courses</HashLink>
        </Button>
      </div>

      <div id="organisations" className="scroll-mt-20 border-t border-slate-200 bg-[#F8FAFC]" data-testid="pricing-organisations">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:pb-24">
          <h3 className="text-xl font-semibold text-slate-900">For organisations</h3>
          <p className="mt-2 text-base text-slate-700">Develop skills, close training gaps and track workforce learning.</p>
          <ul className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {ORG_PLANS.map((p, i) => <PlanCard key={p.name} plan={p} testid={`pricing-org-card-${i}`} />)}
          </ul>
        </div>
      </div>
    </section>
  );
}
